import analytics from 'aria-viewer/analytics/utils/kinesisAnalytics'
import * as EVENTS from 'aria-viewer/analytics/constants/mobileInteractions'
import * as COMMON_EVENTS from 'aria-viewer/analytics/constants/'
import { VR_BUTTON_LOCATION } from 'aria-viewer/tour/components/buttons/vrButton'
import { VideoPlayerActions } from 'aria-viewer/players/videoplayer'
import { PANORAMA_DRAG_END, PANORAMA_TAP } from 'aria-viewer/players/panoplayer/actions/panoramaActions'
import {
    HOTSPOT_CLICKED,
    HOTSPOT_MODAL_SCROLLED,
    HOTSPOT_TYPES,
    HOTSPOT_MEDIA_VIEWED,
} from 'aria-viewer/interactive-element/actions/hotspotActions'
import { TOUR_DRAWER, MAP_DRAWER, STOP_LIST_DRAWER, LIBRARY_DRAWER } from 'aria-viewer/drawer/drawerMenuActions'
import { SHOW_SLIDE_PANEL, HIDE_SLIDE_PANEL } from 'aria-viewer/slidepanel/slidePanelActions'
import { SET_CONTEXT_MENU, SHARE_MENU } from 'aria-viewer/contextmenu/contextMenuActions'
import { InteractiveElementActions } from 'aria-viewer/interactive-element'
import {
    ANALYTICS_EXP_SELECTED,
    ANALYTICS_VR_BUTTON_TAPPED,
    ANALYTICS_CONTEXTMENU_ITEM_CLICKED,
    ANALYTICS_ACTION_BUTTON_CLICKED,
    ANALYTICS_DESCRIPTION_SCROLL_COMPLETE,
    ANALYTICS_REGISTRATION_EXTERNAL_LINK_CLICKED,
    ANALYTICS_SET_STOP_INITIATED_BY,
    ANALYTICS_REGISTRATION_SUBMITTED_SUCCESS,
    ANALYTICS_CHANGE_STOPCARD,
    ANALYTICS_STOPCARD_SUPPMEDIA_SWIPE,
    ANALYTICS_FIRST_STOP_VIEWED,
    ANALYTICS_SUPERCONTENT_STARTED,
} from 'aria-viewer/analytics/analyticActions'
import {
    TourActions,
    KINESIS_TOKEN,
    STOP_ANALYTICS_INITIATED_BY,
    SHARE_TYPES,
    CUSTOM_MEDIA_LABELS,
    MEDIA_TYPES,
} from 'aria-viewer/tour'
import {
    SHOW_SUPPLEMENTAL_MEDIA_VIEW,
    VIDEO_FULL_SCREEN,
    SET_SUPPLEMENTAL_MEDIA_TAB,
    SHOW_INFO,
} from 'aria-viewer/players/supplementalmedia/supplementalMediaActions'
import StopCardStates from 'aria-viewer/stopselector/constants/StopCardStates'
import { SHOW_STOP_CARDS } from 'aria-viewer/stopselector/actions/stopCardsActions'
import { AUDIO_STOP, AUDIO_PLAY, AUDIO_TOUR } from 'aria-viewer/audiomanager/audioManagerActions'
import { SET_MODAL_STACK } from 'aria-viewer/modal/modalActions'
import ModalContentTypes from 'aria-viewer/modal/constants/ModalContentTypes'
import { CTA_BUTTON_CLICKED, WELCOME_PROMPT, TOUR_STARTED } from 'aria-viewer/modal/constants/ModalSources'
import { SET_MAP_ZOOM } from 'aria-viewer/map/mapActions'
import { LANGUAGE_CHANGE } from 'aria-viewer/language/languageActions'
import { trackInteractionEvent, onPreProcess } from './interactionHelpers'
import { combineAnalytics } from 'aria-viewer/core/middleware/analyticsMiddleware'

import mobileAnalytics from '../analytics/mobileAnalytics'

const getInitiatedObj = initiated => ({
    Initiated_by: initiated,
})

/**
 * returns initiatedby for cta buttons
 */
const getCTAInitiatedBy = state => {
    const initTypes = {
        drawer: 'Drawer',
        welcome: 'Welcome',
        exp: 'Experience Home',
    }

    const isSlidePanelOpen = state.getIn(['slidePanel', 'isSlidePanelOpen']) == SHOW_SLIDE_PANEL

    const tour = state.get('tour')
    const tourWelcome = tour.get('welcomePrompt')
    const registrationWelcome = tour.getIn(['registration', 'welcomePrompt'])

    if (isSlidePanelOpen) {
        return getInitiatedObj(initTypes['drawer'])
    } else if (tourWelcome || registrationWelcome) {
        return getInitiatedObj(initTypes['welcome'])
    } else {
        return getInitiatedObj(initTypes['exp'])
    }
}

const getSuppMediaInitiatedBy = (state, action) => {
    const initTypes = {
        map: 'Map',
        slidePanel: 'Slide Out Panel',
        stopCard: 'Stop Card',
    }

    if (action.showAllMedia) {
        return getInitiatedObj(initTypes.slidePanel)
    } else {
        const currentDrawer = state.getIn(['drawerMenu', 'name'])

        if (currentDrawer == MAP_DRAWER) {
            return getInitiatedObj(initTypes.map)
        } else {
            return getInitiatedObj(initTypes.stopCard)
        }
    }
}

const getRegistrationInitBy = initBy => {
    const initByTypes = {
        [CTA_BUTTON_CLICKED]: 'Call To Action',
        [TOUR_STARTED]: 'Auto Launch',
        [WELCOME_PROMPT]: 'Welcome Prompt',
    }

    return getInitiatedObj(initByTypes[initBy])
}

const interactionMappers = {
    [TourActions.SKIPPED_ALT_MAIN_MEDIA]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_INTROVIDEO_SKIPPED)(getState)
    },

    [VideoPlayerActions.SET_CURRENT_QUALITY]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_VIDEO_QUALITYCHANGE)(getState)
    },

    [VideoPlayerActions.PLAY_VIDEO]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_VIDEO_START)(getState)
    },

    [VideoPlayerActions.VIDEO_DRAG_END]: (getState, action) => {
        const state = getState()

        const altMainMedia = state.getIn(['tour', 'altMainMedia'])

        if (altMainMedia && altMainMedia.skipButtonTitle == CUSTOM_MEDIA_LABELS.BACK_TO_SCENE)
            return trackInteractionEvent(EVENTS.M_TRIGGER_VIDEO_PAN)(getState)
        else return trackInteractionEvent(EVENTS.M_VIDEO_PAN)(getState)
    },

    // This interaction needs to be properly tested after the video player in the dev environment
    // is fixed.
    [VideoPlayerActions.SET_VIDEO_VOLUME]: (getState, action) => {
        // Muted
        if (action.volume == 0) {
            return trackInteractionEvent(EVENTS.M_VIDEO_MUTE)(getState)
        } else if (action.volume > 0) {
            // Unmuted
            return trackInteractionEvent(EVENTS.M_VIDEO_UNMUTE)(getState)
        }
    },

    [VideoPlayerActions.SEEKED]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_VIDEO_SEEK)(getState)
    },

    [PANORAMA_DRAG_END]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_PANO_PAN)(getState)
    },

    [PANORAMA_TAP]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_PANO_TAP)(getState)
    },

    [TOUR_DRAWER]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_LOC_DROPDOWN_TAPPED)(getState)
    },

    [ANALYTICS_EXP_SELECTED]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_LOC_DROPDOWN_LOCTAPPED)(getState)
    },

    [ANALYTICS_VR_BUTTON_TAPPED]: (getState, action) => {
        let eventToTrack
        switch (action.buttonLocation) {
            case VR_BUTTON_LOCATION.mobile.clientInfoMenu:
                eventToTrack = EVENTS.M_SLIDEOUT_VRBUTTON_TAPPED
                break
            case VR_BUTTON_LOCATION.mobile.tourButtonPanel:
                eventToTrack = EVENTS.M_VRBUTTON_TAPPED
                break
            default:
                eventToTrack = null
        }
        return eventToTrack && trackInteractionEvent(eventToTrack)(getState)
    },

    [InteractiveElementActions.SET_OPEN_TRIGGER]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_TRIGGER_POPUP_LAUNCHED)(getState)
    },

    [InteractiveElementActions.TRIGGER_CLICKED]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_TRIGGER_POPUP_ENTER_TAPPED)(getState)
    },

    [TourActions.NEXT_POINT]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_SCENE_NEXTARROW_TAPPED)(getState)
    },

    [TourActions.PREV_POINT]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_SCENE_BACKARROW_TAPPED)(getState)
    },

    [HOTSPOT_CLICKED]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_HOTSPOT_TAPPED)(getState)
    },

    [TourActions.SHOW_ALT_MEDIA]: (getState, action) => {
        const { skipButtonTitle } = action.media || {}

        if (skipButtonTitle == CUSTOM_MEDIA_LABELS.BACK_TO_SCENE)
            return trackInteractionEvent(EVENTS.M_TRIGGER_VIDEO_STARTED)(getState)
    },

    [HOTSPOT_MODAL_SCROLLED]: (getState, action) => {
        let eventToTrack

        switch (action.hotspotType) {
            case HOTSPOT_TYPES.tooltip:
                eventToTrack = EVENTS.M_HOTSPOT_TEXT_COMPLETED
                break
            case HOTSPOT_TYPES.photo:
                eventToTrack = EVENTS.M_HOTSPOT_IMAGEDESC_COMPLETED
                break
            case HOTSPOT_TYPES.video:
            case HOTSPOT_TYPES.video360:
                eventToTrack = EVENTS.M_HOTSPOT_VIDEODESC_COMPLETED
                break
            default:
                eventToTrack = null
        }

        return eventToTrack && trackInteractionEvent(eventToTrack)(getState)
    },

    [ANALYTICS_ACTION_BUTTON_CLICKED]: (getState, action) => {
        const { name, color, actionName } = action

        const properties = {
            CTA_type: actionName,
            CTA_name: name,
            Color: color,
        }

        const additionalProperties = {
            ...getCTAInitiatedBy(getState()),
            ...properties,
        }

        return trackInteractionEvent(EVENTS.M_CTABUTTON_TAPPED, additionalProperties)(getState)
    },

    [SHOW_SLIDE_PANEL]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_MENU_TAPPED)(getState)
    },

    [HIDE_SLIDE_PANEL]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_MENU_TAPPED)(getState)
    },

    [ANALYTICS_CONTEXTMENU_ITEM_CLICKED]: (getState, action) => {
        let eventToTrack

        switch (action.itemName) {
            case SHARE_TYPES.link:
                eventToTrack = EVENTS.M_MENU_LINK
                break
            case SHARE_TYPES.email:
                eventToTrack = EVENTS.M_MENU_EMAIL
                break
            case SHARE_TYPES.twitter:
                eventToTrack = EVENTS.M_MENU_TWITTER
                break
            case SHARE_TYPES.linkedin:
                eventToTrack = EVENTS.M_MENU_LINKEDIN
                break
            case SHARE_TYPES.facebook:
                eventToTrack = EVENTS.M_MENU_FACEBOOK
                break
            default:
                eventToTrack = null
        }

        return eventToTrack && trackInteractionEvent(eventToTrack)(getState)
    },

    [HOTSPOT_MEDIA_VIEWED]: (getState, action) => {
        let eventToTrack

        switch (action.hotspotType) {
            case HOTSPOT_TYPES.panorama:
                eventToTrack = EVENTS.M_HOTSPOT_PANO_VIEWED
                break
            case HOTSPOT_TYPES.photo:
                eventToTrack = EVENTS.M_HOTSPOT_IMAGE_VIEWED
                break
            case HOTSPOT_TYPES.video:
            case HOTSPOT_TYPES.video360:
                eventToTrack = EVENTS.M_HOTSPOT_VIDEO_VIEWED
                break
            default:
                eventToTrack = null
        }

        return eventToTrack && trackInteractionEvent(eventToTrack)(getState)
    },

    [SET_CONTEXT_MENU]: (getState, action) => {
        let eventToTrack

        const state = getState()

        const currentContextMenu = state.getIn(['contextMenu', 'name'])
        const stopCardState = state.getIn(['stopCards', 'state'])

        switch (currentContextMenu) {
            case SHARE_MENU:
                eventToTrack =
                    stopCardState == StopCardStates.SHOW ? EVENTS.M_STOPCARDS_SHARE_TAPPED : EVENTS.M_MENU_SHARE_TAPPED
                break
            default:
                eventToTrack = null
        }

        return eventToTrack && trackInteractionEvent(eventToTrack)(getState)
    },

    [TourActions.STOP_CHANGED]: (getState, action) => {
        const isTourGuideAudioPlaying = getState().getIn(['audioManager', 'state', 'AUDIO_TOUR']) === AUDIO_PLAY

        return trackInteractionEvent(EVENTS.M_SCENE_ENTER, {
            IsTourGuideAudioPlaying: isTourGuideAudioPlaying,
        })(getState)
    },

    [ANALYTICS_SUPERCONTENT_STARTED]: (getState, action) => {
        const sceneChangedEvent = interactionMappers[TourActions.STOP_CHANGED](getState, action)

        return [trackInteractionEvent(COMMON_EVENTS.EXPERIENCE_RESTARTED)(getState), sceneChangedEvent]
    },

    [TourActions.USER_IS_IDLE]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_SCENE_10SECONDS)(getState)
    },

    [STOP_LIST_DRAWER]: (getState, action) => {
        const state = getState()

        const stopCardState = state.get(['stopCards', 'state'])

        let eventToTrack

        switch (stopCardState) {
            case StopCardStates.SHOW:
                eventToTrack = EVENTS.M_STOPCARDS_STOPLIST_TAPPED
                break
            case StopCardStates.LOWER:
                eventToTrack = EVENTS.M_SCENENAV_STOPLIST_TAPPED
                break
            default:
                eventToTrack = null
        }

        return eventToTrack && trackInteractionEvent(eventToTrack)(getState)
    },

    [SHOW_STOP_CARDS]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_SCENE_MOREINFO_SWIPE)(getState)
    },

    [ANALYTICS_CHANGE_STOPCARD]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_STOPCARDS_TAPPED)(getState)
    },

    [ANALYTICS_STOPCARD_SUPPMEDIA_SWIPE]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_STOPCARDS_SUPPMEDIA_SWIPE)(getState)
    },

    [ANALYTICS_FIRST_STOP_VIEWED]: (getState, action) => {
        return trackInteractionEvent(ANALYTICS_FIRST_STOP_VIEWED)(getState)
    },

    [AUDIO_STOP]: (getState, action) => {
        if (action.name == AUDIO_TOUR) return trackInteractionEvent(EVENTS.M_TOURGUIDE_OFF)(getState)
    },

    [ANALYTICS_DESCRIPTION_SCROLL_COMPLETE]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_STOPCARDS_MOREINFO_COMPLETED)(getState)
    },

    [ANALYTICS_REGISTRATION_EXTERNAL_LINK_CLICKED]: (getState, action) => {
        return trackInteractionEvent(action.eventName)(getState)
    },

    [LANGUAGE_CHANGE]: (getState, action) => {
        const state = getState()

        const stopCardState = state.getIn(['stopCards', 'state'])

        let eventToTrack

        switch (stopCardState) {
            case StopCardStates.SHOW:
                eventToTrack = EVENTS.M_STOPCARDS_LANGUAGE_CHANGED
                break
            case StopCardStates.LOWER:
                eventToTrack = EVENTS.M_TOURGUIDE_CHANGELANGUAGE
                break
            default:
                eventToTrack = null
        }
        return eventToTrack && trackInteractionEvent(eventToTrack)(getState)
    },

    [SHOW_SUPPLEMENTAL_MEDIA_VIEW]: (getState, action) => {
        const additionalProperties = getSuppMediaInitiatedBy(getState(), action)

        return trackInteractionEvent(EVENTS.M_SUPPMEDIA_TAPPED, additionalProperties)(getState)
    },

    [LIBRARY_DRAWER]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_SUPPMEDIA_BOX_TAPPED)(getState)
    },

    [SET_SUPPLEMENTAL_MEDIA_TAB]: (getState, action) => {
        // SET_SUPPLEMENTAL_MEDIA_TAB gets called when you click browse all media
        // in the slide out panel and it passes option as ""
        // if option is not empty string track the event
        if (action.option != '') {
            return trackInteractionEvent(EVENTS.M_SUPPMEDIA_CATEGORY_TAPPED)(getState)
        }
    },

    [SHOW_INFO]: (getState, action) => {
        const supplementalMediaState = getState().get('supplementalMedia')
        const currentSuppMedia = supplementalMediaState.get('media')

        let eventToTrack

        if (currentSuppMedia) {
            switch (currentSuppMedia.mediaType) {
                case MEDIA_TYPES.panorama:
                    eventToTrack = EVENTS.M_SUPPMEDIA_PANO_INFO_TAPPED
                    break
                case MEDIA_TYPES.photo:
                    eventToTrack = EVENTS.M_SUPPMEDIA_PHOTO_INFO_TAPPED
                    break
                case MEDIA_TYPES.video:
                case MEDIA_TYPES.video360:
                    eventToTrack = EVENTS.M_SUPPMEDIA_VIDEO_INFO_TAPPED
                    break
                default:
                    eventToTrack = null
            }
        }

        return eventToTrack && trackInteractionEvent(eventToTrack)(getState)
    },

    [VIDEO_FULL_SCREEN]: (getState, action) => {
        return trackInteractionEvent(EVENTS.M_SUPPMEDIA_VIDEO_EXPAND)(getState)
    },

    [ANALYTICS_SET_STOP_INITIATED_BY]: (getState, action) => {
        const { initiatedBy } = action

        if (initiatedBy == STOP_ANALYTICS_INITIATED_BY.map)
            return trackInteractionEvent(EVENTS.M_MAP_STOP_TAPPED)(getState)
    },

    [SET_MAP_ZOOM]: (getState, action) => {
        const state = getState()
        const currentZoomLevel = state.getIn(['map', 'zoomBounds', 'level'])
        const {
            zoom: { level },
        } = action

        const zoomEvent = currentZoomLevel > level ? EVENTS.M_MAP_ZOOM_OUT : EVENTS.M_MAP_ZOOM_IN

        return trackInteractionEvent(zoomEvent)(getState)
    },

    [ANALYTICS_REGISTRATION_SUBMITTED_SUCCESS]: (getState, action) => {
        const additionalProperties = getCTAInitiatedBy(getState())

        return trackInteractionEvent(EVENTS.M_REGISTRATIONSUBMITTED, additionalProperties)(getState)
    },

    [SET_MODAL_STACK]: (getState, action) => {
        // currently this is just for to track registraion
        // will need to update functionality in the future
        const currentModal = getState().get('modalShown')

        if (currentModal && currentModal.content.type == ModalContentTypes.REGISTRATION) {
            let additionalProperties = getRegistrationInitBy(currentModal.content.initBy)

            return trackInteractionEvent(EVENTS.M_REGISTRATIONBUTTON_TAPPED, additionalProperties)(getState)
        }
    },
}

const customMapper = {
    analytics: analytics,

    token: KINESIS_TOKEN,

    mapper: combineAnalytics(mobileAnalytics.mapper, interactionMappers),

    onPreProcess: onPreProcess,
}

export default customMapper
